import type { Work } from '../types/Work'
import type { Project } from '../types/Project'
import type { ColorOptionProduct } from '../types/ColorOptionProduct'

/**
 * Validates and converts input to a positive integer.
 * @param input - The value to validate and convert
 * @returns The validated positive integer
 * @throws Error if input cannot be converted to a positive integer
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const asPositiveInteger = (input: any): number => {
  let value = input
  if (typeof value === 'string') value = parseInt(value, 10)
  if (typeof value === 'number') {
    if (Number.isInteger(value) && value > 0) {
      return value
    }
  }
  throw new Error('Unable to validate or convert the input to a positive integer.')
}

/**
 * Checks if a sized good work item has valid quantities.
 * @param work - The work item to validate
 * @returns True if the work has valid quantities for at least one size
 */
export const sizedGoodWorkHasQuantities = (work: Work): boolean => {
  try {
    const qtyString = work?.resources?.['qty']
    if (!qtyString) {
      return false
    }
    const quantityPerSize = JSON.parse(qtyString)

    if (typeof quantityPerSize !== 'object' || Object.keys(quantityPerSize).length === 0) {
      return false
    }

    // Check if at least one size has a positive quantity
    for (const [, value] of Object.entries<number>(quantityPerSize)) {
      try {
        if (asPositiveInteger(value) > 0) {
          return true
        }
      } catch {
        // Skip invalid values
        continue
      }
    }

    return false
  } catch {
    return false // we encountered an invalid quantity value (The QSP should be able to fix it)
  }
}

/**
 * Checks if a hard good work item has valid quantities.
 * @param work - The work item to validate
 * @returns True if the work has a valid quantity
 */
export const hardGoodWorkHasQuantities = (work: Work): boolean => {
  try {
    const qtyString = work.resources['qty']
    if (!qtyString) {
      return false
    }
    const qty = JSON.parse(qtyString)

    return asPositiveInteger(qty) > 0
  } catch {
    return false // we encountered an invalid quantity value (The QSP should be able to fix it)
  }
}

/**
 * Determines if a project is eligible to be added directly to cart without user interaction.
 * A project can be auto-added if:
 * - It has work and no related products
 * - It has work and is not a sized good with color options
 * - It has valid quantities set
 *
 * Note: Teams projects should be filtered out before calling this function.
 *
 * @param project - The project to evaluate
 * @param colorOptions - Optional list of color option products associated with the project
 * @returns True if the project can be automatically added to cart
 */
export const canAddDirectlyToCart = (project?: Project, colorOptions?: ColorOptionProduct[]): boolean => {
  if (project && project.work && !project.hasRelatedProducts) {
    const { work, pageMode } = project
    if (work.resources?.['qty']) {
      if (pageMode === 'Sized Good') {
        if (colorOptions && colorOptions.length > 0) {
          return false
        }
        if (sizedGoodWorkHasQuantities(work)) {
          return true
        }
      } else {
        if (hardGoodWorkHasQuantities(work)) {
          return true
        }
      }
    }
  }
  return false
}
